import { Item, ItemGroupPreview, ItemsResponseDTO } from "@dto/architecture.dto";
import { AxiosError, AxiosResponse } from "axios";
import { useEffect, useMemo, useState } from "react";
import { Log } from "src/service/log.service";
import { GLOB } from "src/util/Glob";
import { AdditionalPayload, ApiPrams, invalidHwProps, ruleHwType } from "../XmRule";
import { regexApi } from "../comp/preview/Preview";

function useGetAllItemsRegex(cls: string, hw_type: string, subsystem: string, linuxExclude: ItemGroupPreview['linuxExclude'], additionalApiParm: ApiPrams = {}, additionalPayload: AdditionalPayload = {}): [Item[], boolean, Promise<AxiosResponse<ItemsResponseDTO, ItemGroupPreview>>] {
  const invalidBaseProps = invalidHwProps({ cls, hw_type, subsystem });
  const withDeleted = additionalPayload.withDeleted ?? false;
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [promise, setPromise] = useState<Promise<AxiosResponse<ItemsResponseDTO, ItemGroupPreview>>>();
  const [fetchDeleted, setFetchDeleted] = useState(withDeleted);
  const [baseDeps, setBaseDeps] = useState({ hw_type, subsystem });

  if (withDeleted && !fetchDeleted)
    setFetchDeleted(true);

  if (baseDeps.hw_type !== hw_type || baseDeps.subsystem !== subsystem) {
    setBaseDeps({ hw_type, subsystem });
    setFetchDeleted(withDeleted);
  }

  useEffect(() => {
    if (invalidBaseProps)
      return;

    const processData = ({ data: { data } }: AxiosResponse<ItemsResponseDTO, ItemGroupPreview>) => {
      setItems(data.sort((a, b) => GLOB.naturalSort(a.label, b.label)));
    };

    setIsLoading(true);
    const controller = new AbortController();
    const postPromise = regexApi(
      {
        class: cls,
        subsystem,
        hw_type: ruleHwType(cls, hw_type),
        regex: [".*"],
        linuxExclude,
        ...additionalPayload,
        withDeleted: fetchDeleted
      },
      { signal: controller.signal, params: additionalApiParm },
    );

    postPromise.then(
      processData,
      (reason: AxiosError) => Log.error('Failed to retrieve items!', reason)
    ).finally(() => setIsLoading(false));

    setPromise(postPromise);

    return () => controller.abort();
  }, [hw_type, subsystem, fetchDeleted]);

  const visibleItems = useMemo(
    () => withDeleted ? items : items.filter(item => !item.deleted),
    [items, withDeleted]
  );

  return [visibleItems, isLoading, promise];
}

export default useGetAllItemsRegex;